<script setup>
function scrollTo(event) {
    const href = event.target.getAttribute('href');
    if (href && href.startsWith('#')) {
        event.preventDefault();
        const el = document.getElementById(href.substring(1));
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }
}
</script>

<template>
<div class="bv-wrapper">
<!-- Header -->
<div class="header">
    <h1>Brukerveiledning Backoffice</h1>
    <p class="subtitle">Komplett veiledning for administratorer av CAGISTA Backoffice</p>
    <div class="header-meta">
        <span>Versjon 1.0</span>
        <span>02. januar 2026</span>
        <span>CAGISTA AS</span>
    </div>
</div>

<div class="container">

<!-- Sidebar -->
<aside class="sidebar">
    <nav class="toc" @click="scrollTo">
        <h3>Innholdsfortegnelse</h3>
        <ol>
            <li><a href="#kap1">Innledning</a></li>
            <li><a href="#kap2">Innlogging</a></li>
            <li><a href="#kap3">Oversikt (Dashboard)</a></li>
            <li><a href="#kap4">Ordrer</a></li>
            <li><a href="#kap5">Rapporter</a></li>
            <li><a href="#kap6">Produkter</a></li>
            <li><a href="#kap7">Lojalitet</a></li>
            <li><a href="#kap8">Brukere</a></li>
            <li><a href="#kap9">Innsikt (Analytics)</a></li>
            <li><a href="#kap10">Innstillinger</a></li>
            <li><a href="#kap11">Support</a></li>
        </ol>
    </nav>
</aside>

<!-- Main Content -->
<main class="main">

<!-- 1. Innledning -->
<div class="section" id="kap1">
    <h2><span class="chapter-num">1</span> Innledning</h2>
    <p>CAGISTA Backoffice er et nettbasert administrasjonssystem for CAGISTA-plattformen. Her kan du som administrator styre alle aspekter av din virksomhet, fra ordrehåndtering og produktadministrasjon til rapportering og innstillinger.</p>
    <p>Denne veiledningen dekker alle funksjoner tilgjengelig for brukere med <strong>ADMIN</strong>-rollen. Administratorer har full tilgang til samtlige funksjoner i Backoffice.</p>

    <h3>Systemkrav</h3>
    <table>
        <thead><tr><th>Krav</th><th>Spesifikasjon</th></tr></thead>
        <tbody>
            <tr><td><strong>Nettleser</strong></td><td>Google Chrome, Mozilla Firefox, Safari eller Microsoft Edge (siste versjon)</td></tr>
            <tr><td><strong>Internett</strong></td><td>Stabil internettforbindelse</td></tr>
            <tr><td><strong>Skjerm</strong></td><td>Minimum 1024 x 768 piksler (anbefalt 1920 x 1080)</td></tr>
        </tbody>
    </table>

    <h3>Menyoversikt</h3>
    <p>Navigasjonsmenyen øverst gir tilgang til følgende hovedseksjoner:</p>
    <table>
        <thead><tr><th>Meny</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Oversikt</strong></td><td>Dashboard med sanntids KPI-er og statusoversikt</td></tr>
            <tr><td><strong>Ordrer</strong></td><td>Ordre- og refunderingshåndtering</td></tr>
            <tr><td><strong>Rapporter</strong></td><td>X-rapport, Z-rapport, omsetning, SAF-T m.m.</td></tr>
            <tr><td><strong>Produkter</strong></td><td>Produkt-, kategori- og tilleggsadministrasjon</td></tr>
            <tr><td><strong>Lojalitet</strong></td><td>Rabatter og bonusprogrammer</td></tr>
            <tr><td><strong>Brukere</strong></td><td>Brukeradministrasjon og roller</td></tr>
            <tr><td><strong>Innsikt</strong></td><td>Forretningsanalytikk og trender</td></tr>
            <tr><td><strong>Innstillinger</strong></td><td>Alle konfigurasjoner for virksomheten</td></tr>
            <tr><td><strong>Support</strong></td><td>Brukerveiledninger, hjelpevideo og bokettersyn</td></tr>
        </tbody>
    </table>

    <div class="info-box">
        <strong>Rollebasert tilgang:</strong> Brukere med EMPLOYEE-rollen har begrenset tilgang og kan kun se Oversikt, Ordrer (uten refundering), Produkter (kun visning) og Stengte dager. Alle andre funksjoner er forbeholdt ADMIN-brukere.
    </div>
</div>

<!-- 2. Innlogging -->
<div class="section" id="kap2">
    <h2><span class="chapter-num">2</span> Innlogging</h2>

    <h3>2.1 Logge inn</h3>
    <ol>
        <li>Åpne Backoffice-nettadressen i nettleseren</li>
        <li>Skriv inn ditt <strong>brukernavn</strong> og <strong>passord</strong></li>
        <li>Klikk <strong>Logg inn</strong></li>
        <li>Du blir videresendt til dashboardet (Oversikt)</li>
    </ol>

    <h3>2.2 Logge ut</h3>
    <p>Klikk på <strong>Logg ut</strong>-knappen øverst til høyre i navigasjonsmenyen. Du blir sendt tilbake til innloggingssiden.</p>

    <h3>2.3 Min profil</h3>
    <p>Klikk på brukernavnet ditt øverst til høyre for å se og redigere din profilinformasjon.</p>
</div>

<!-- 3. Oversikt -->
<div class="section" id="kap3">
    <h2><span class="chapter-num">3</span> Oversikt (Dashboard)</h2>
    <p>Dashboardet gir deg en sanntidsoversikt over virksomhetens drift. Her finner du viktige nøkkeltall og kontrollverktøy.</p>

    <h3>3.1 Avdelingsvelger</h3>
    <p>Dersom du har flere avdelinger (lokasjoner), kan du bruke avdelingsvelgeren øverst for å filtrere data for en spesifikk avdeling, eller velge <strong>Alle avdelinger</strong> for en samlet oversikt.</p>

    <h3>3.2 Datovelger</h3>
    <p>Velg dato for å se historiske data. Som standard vises dagens tall.</p>

    <h3>3.3 KPI-kort</h3>
    <p>Dashboardet viser følgende nøkkeltall:</p>
    <table>
        <thead><tr><th>KPI</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Total omsetning</strong></td><td>Samlet salg for valgt dato, med trendsammenligning</td></tr>
            <tr><td><strong>Antall ordrer</strong></td><td>Totalt antall ordrer, med trendsammenligning</td></tr>
            <tr><td><strong>Gjennomsnittlig ordreverdi</strong></td><td>Snittverdi per ordre</td></tr>
            <tr><td><strong>Rushtid</strong></td><td>Tidspunkt med flest ordrer</td></tr>
        </tbody>
    </table>

    <h3>3.4 Kontrollpanel</h3>
    <p>Rask tilgang til driftskontroller:</p>
    <ul>
        <li><strong>Stengte dager</strong> – Se og administrere planlagte stengte dager</li>
        <li><strong>Åpning/stenging</strong> – Se om avdelingen er åpen eller stengt</li>
        <li><strong>Kategoriinformasjon</strong> – Oversikt over produktkategorier</li>
        <li><strong>Utsolgte varer</strong> – Se hvilke varer som er markert utsolgt</li>
    </ul>

    <h3>3.5 Avdelingsoversikt (flerlokasjoner)</h3>
    <p>Dersom du har flere avdelinger, vises et rutenett med informasjon per avdeling:</p>
    <ul>
        <li>Omsetning i dag sammenlignet med i går</li>
        <li>Antall ordrer</li>
        <li>Rushtid per avdeling</li>
        <li>Status (åpen/stengt)</li>
    </ul>
</div>

<!-- 4. Ordrer -->
<div class="section" id="kap4">
    <h2><span class="chapter-num">4</span> Ordrer</h2>
    <p>Ordresiden gir deg full oversikt over alle ordrer fra alle salgskanaler. Naviger hit via <strong>Ordrer</strong> i toppmenyen.</p>

    <h3>4.1 Søk og filtrering</h3>
    <p>Du kan filtrere ordrelisten med følgende kriterier:</p>
    <table>
        <thead><tr><th>Filter</th><th>Alternativer</th></tr></thead>
        <tbody>
            <tr><td><strong>Søk</strong></td><td>Ordrenummer, kundenavn eller telefonnummer</td></tr>
            <tr><td><strong>Kanal</strong></td><td>Nettbutikk, Kiosk, Kasse (POS)</td></tr>
            <tr><td><strong>Ordretype</strong></td><td>Spise her, Henting, Levering</td></tr>
            <tr><td><strong>Ordrestatus</strong></td><td>Bestilt, Bekreftet, Fullført, Kansellert</td></tr>
            <tr><td><strong>Betalingsstatus</strong></td><td>Betalt, Ikke betalt</td></tr>
            <tr><td><strong>Datoperiode</strong></td><td>Fra-dato og til-dato</td></tr>
        </tbody>
    </table>

    <h3>4.2 Ordrekort</h3>
    <p>Hver ordre vises som et kort med følgende informasjon:</p>
    <ul>
        <li>Ordrenummer og ordretype</li>
        <li>Tidspunkt for bestilling og henting/levering</li>
        <li>Kundenavn og telefonnummer</li>
        <li>Ordrestatus med visuell indikator</li>
        <li>Betalingsmetode og betalingsstatus</li>
    </ul>

    <h3>4.3 Ordredetaljer</h3>
    <p>Klikk på et ordrekort for å se fullstendige detaljer:</p>
    <ul>
        <li>Komplett ordreinnhold med priser og tillegg</li>
        <li>Kundeinformasjon</li>
        <li>Statushistorikk</li>
        <li>Betalingsdetaljer</li>
    </ul>

    <h3>4.4 Refundering</h3>
    <p>Som ADMIN kan du refundere betalte ordrer:</p>
    <ol>
        <li>Åpne ordredetaljene for en betalt ordre</li>
        <li>Klikk <strong>Refunder</strong></li>
        <li>Refunderingen behandles avhengig av betalingsmetode:
            <ul>
                <li><strong>Nettbetaling</strong> – Automatisk refundering via betalingsleverandøren</li>
                <li><strong>SoftPay/Viva</strong> – Refundering via betalingsterminalen</li>
                <li><strong>Kontant</strong> – Manuell refundering</li>
            </ul>
        </li>
        <li>Ordren markeres som <strong>REFUNDERT</strong></li>
    </ol>

    <div class="info-box">
        <strong>Merk:</strong> Kun ADMIN-brukere har tilgang til refunderingsfunksjonen. EMPLOYEE-brukere kan kun se ordrer.
    </div>
</div>

<!-- 5. Rapporter -->
<div class="section" id="kap5">
    <h2><span class="chapter-num">5</span> Rapporter</h2>
    <p>Rapportseksjonen gir tilgang til alle økonomiske og operasjonelle rapporter. Naviger via <strong>Rapporter</strong> i toppmenyen.</p>

    <h3>5.1 X-Rapport</h3>
    <p>Foreløpig salgsrapport for inneværende periode.</p>
    <ol>
        <li>Velg <strong>avdeling</strong> og <strong>terminal</strong></li>
        <li>Rapporten viser akkumulerte salgstall inkludert:
            <ul>
                <li>Omsetning per betalingsmetode (Vipps, kontant, kort m.m.)</li>
                <li>MVA-beregninger</li>
                <li>Ordrestatistikk</li>
            </ul>
        </li>
        <li>Fra X-rapporten kan du opprette en <strong>Z-rapport</strong> (dagsoppgjør)</li>
    </ol>

    <h3>5.2 Z-Rapport (Dagsoppgjør)</h3>
    <p>Historiske daglige oppgjørsrapporter.</p>
    <ol>
        <li>Velg <strong>avdeling</strong>, <strong>terminal</strong> og <strong>dato</strong></li>
        <li>Se tidligere Z-rapporter med fullstendig salgsoversikt</li>
        <li><strong>Eksporter som PDF</strong> – Last ned enkeltstående eller flere Z-rapporter som PDF</li>
    </ol>
    <p>Z-rapporten inneholder:</p>
    <ul>
        <li>Komplett salgssammendrag</li>
        <li>Fordeling per betalingsmetode</li>
        <li>MVA- og skatteinformasjon</li>
        <li>Åpnings- og lukkingskontanter</li>
        <li>Rabatter, annulleringer og refunderinger</li>
    </ul>

    <h3>5.3 Omsetningsrapport</h3>
    <p>Periodisk salgsanalyse med total omsetning og antall ordrer for en valgt periode.</p>
    <ol>
        <li>Velg <strong>avdeling</strong>, <strong>terminal</strong> og <strong>datoperiode</strong></li>
        <li>Se total omsetning og daglig nedbrytning</li>
        <li>MVA-beregninger per periode</li>
    </ol>

    <h3>5.4 Kontantbeholdning</h3>
    <p>Spor kontanter i kassen over tid. Velg terminal og datoperiode for å se kontantakkumulering og identifisere avvik.</p>

    <h3>5.5 Alkoholrapport</h3>
    <p>Spesialisert rapport for alkoholsalg, påkrevd for regulatorisk etterlevelse. Velg datoperiode for å se alkoholspesifikke salgstall per terminal.</p>

    <h3>5.6 SMS-Rapport</h3>
    <p>Oversikt over SMS-bekreftelser sendt til kunder. Tilgjengelig kun dersom SMS-tjenesten er aktivert.</p>

    <h3>5.7 SAF-T Rapport</h3>
    <p>Eksport av data i SAF-T-format for skattemyndighetene (Skatteetaten).</p>
    <ol>
        <li>Naviger til <strong>Rapporter → SAF-T Rapport</strong></li>
        <li>Velg ønsket <strong>datoperiode</strong></li>
        <li>Systemet genererer en SAF-T-kompatibel fil</li>
        <li>Filen er klar for innsending til skattemyndighetene</li>
    </ol>
    <div class="info-box">
        <strong>SAF-T (Standard Audit File – Tax):</strong> Dette er et standardisert format påkrevd av norske skattemyndigheter for elektronisk rapportering av kassadata. Rapporten inneholder all transaksjonsinformasjon i XML-format.
    </div>
</div>

<!-- 6. Produkter -->
<div class="section" id="kap6">
    <h2><span class="chapter-num">6</span> Produkter</h2>
    <p>Her administrerer du hele produktkatalogen. Naviger via <strong>Produkter</strong> i toppmenyen.</p>

    <h3>6.1 Kategorier</h3>
    <p>Produktene er organisert i kategorier (f.eks. Sushi, Drikke, Dessert).</p>
    <ul>
        <li><strong>Opprett kategori</strong> – Klikk «Ny kategori», angi navn og beskrivelse</li>
        <li><strong>Rediger kategori</strong> – Klikk på kategorien for å endre navn eller beskrivelse</li>
        <li><strong>Slett kategori</strong> – Fjern ubrukte kategorier</li>
        <li><strong>Sorter kategorier</strong> – Endre rekkefølgen kategoriene vises i menyen</li>
    </ul>

    <h3>6.2 Produkter/Varer</h3>
    <p>Administrer enkeltvarer innenfor hver kategori:</p>
    <table>
        <thead><tr><th>Egenskap</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Navn</strong></td><td>Produktets visningsnavn</td></tr>
            <tr><td><strong>Beskrivelse</strong></td><td>Detaljert produktbeskrivelse</td></tr>
            <tr><td><strong>Pris</strong></td><td>Pris med mulighet for ulik Spise her- og Ta med-pris</td></tr>
            <tr><td><strong>MVA-sats</strong></td><td>Merverdiavgiftssats (15 % eller 25 %)</td></tr>
            <tr><td><strong>Bilde</strong></td><td>Produktbilde for visning i meny</td></tr>
            <tr><td><strong>Tilgjengelighet</strong></td><td>Dagbasert tilgjengelighet (man–søn)</td></tr>
        </tbody>
    </table>
    <p>Operasjoner:</p>
    <ul>
        <li><strong>Opprett produkt</strong> – Fyll ut skjemaet med alle egenskaper</li>
        <li><strong>Rediger produkt</strong> – Endre pris, beskrivelse, bilde m.m.</li>
        <li><strong>Slett produkt</strong> – Fjern produktet fra menyen</li>
        <li><strong>Sorter produkter</strong> – Endre visningsrekkefølgen innenfor en kategori</li>
    </ul>

    <h3>6.3 Tilleggsgrupper</h3>
    <p>Tilleggsgrupper lar kundene tilpasse bestillingen (f.eks. «Ekstra tilbehør», «Velg størrelse»).</p>
    <ul>
        <li><strong>Opprett tilleggsgruppe</strong> – Gi gruppen et navn</li>
        <li><strong>Legg til tillegg</strong> – Definer individuelle tilleggsvalg med egen pris</li>
        <li><strong>Sorter tillegg</strong> – Endre rekkefølgen tilleggene vises i</li>
    </ul>

    <div class="info-box">
        <strong>Merk:</strong> EMPLOYEE-brukere kan kun se produkter. Opprettelse, redigering og sletting er forbeholdt ADMIN-brukere.
    </div>
</div>

<!-- 7. Lojalitet -->
<div class="section" id="kap7">
    <h2><span class="chapter-num">7</span> Lojalitet</h2>
    <p>Administrer rabatter og bonusprogrammer for å øke kundelojaliteten. Naviger via <strong>Lojalitet</strong> i toppmenyen.</p>

    <h3>7.1 Rabatter</h3>
    <p>Opprett og administrer prosentbaserte rabatter.</p>
    <ol>
        <li>Klikk <strong>Ny rabatt</strong></li>
        <li>Angi rabattens egenskaper:
            <ul>
                <li><strong>Rabattnavn</strong> – Beskrivende navn</li>
                <li><strong>Rabattprosent</strong> – Prosentandel rabatt</li>
                <li><strong>Gyldighetsperiode</strong> – Fra- og til-dato</li>
                <li><strong>Produkter</strong> – Velg hvilke produkter rabatten gjelder for</li>
                <li><strong>Kanaler</strong> – Nettbutikk, Kiosk og/eller Kasse (POS)</li>
                <li><strong>Avdelinger</strong> – Hvilke avdelinger rabatten gjelder for</li>
            </ul>
        </li>
        <li>Klikk <strong>Lagre</strong></li>
    </ol>

    <h3>7.2 Bonusprogram</h3>
    <p>Sett opp lojalitetsprogrammer der kunder opptjener poeng ved kjøp.</p>
    <ol>
        <li>Klikk <strong>Nytt bonusprogram</strong></li>
        <li>Konfigurer:
            <ul>
                <li><strong>Bonusprosent</strong> – Andel av ordreverdien som returneres som poeng</li>
                <li><strong>Gyldighetsperiode</strong> – Fra- og til-dato</li>
                <li><strong>Avdelinger</strong> – Hvilke avdelinger programmet gjelder for</li>
            </ul>
        </li>
        <li>Klikk <strong>Lagre</strong></li>
    </ol>
    <div class="info-box">
        <strong>Poengverdi:</strong> 1 poeng = 1 NOK. Produkter som allerede har rabatt opptjener ikke bonuspoeng.
    </div>
</div>

<!-- 8. Brukere -->
<div class="section" id="kap8">
    <h2><span class="chapter-num">8</span> Brukere</h2>
    <p>Administrer ansattkontoer og roller. Naviger via <strong>Brukere</strong> i toppmenyen. Denne seksjonen er kun tilgjengelig for ADMIN-brukere.</p>

    <h3>8.1 Ansattliste</h3>
    <p>Tabelloversikt over alle brukere med:</p>
    <ul>
        <li>Fornavn og etternavn</li>
        <li>Brukernavn</li>
        <li>E-postadresse</li>
        <li>Telefonnummer</li>
        <li>Rolle (ADMIN eller EMPLOYEE)</li>
    </ul>

    <h3>8.2 Opprett ny ansatt</h3>
    <ol>
        <li>Klikk <strong>Ny ansatt</strong></li>
        <li>Fyll ut:
            <ul>
                <li>Fornavn og etternavn</li>
                <li>Brukernavn og passord</li>
                <li>E-postadresse og telefonnummer</li>
                <li>Rolle – velg <strong>ADMIN</strong> eller <strong>EMPLOYEE</strong></li>
            </ul>
        </li>
        <li>Klikk <strong>Lagre</strong></li>
    </ol>

    <h3>8.3 Rollebeskrivelser</h3>
    <table>
        <thead><tr><th>Rolle</th><th>Tilgang</th></tr></thead>
        <tbody>
            <tr><td><strong>ADMIN</strong></td><td>Full tilgang til alle funksjoner i Backoffice</td></tr>
            <tr><td><strong>EMPLOYEE</strong></td><td>Begrenset tilgang: Oversikt, Ordrer (uten refundering), Produkter (kun visning), Stengte dager</td></tr>
        </tbody>
    </table>

    <h3>8.4 Rediger/Slett ansatt</h3>
    <ul>
        <li><strong>Rediger</strong> – Klikk på brukerens rad for å endre informasjon eller rolle</li>
        <li><strong>Slett</strong> – Fjern ansattkontoen fra systemet</li>
    </ul>
</div>

<!-- 9. Innsikt -->
<div class="section" id="kap9">
    <h2><span class="chapter-num">9</span> Innsikt (Analytics)</h2>
    <p>Forretningsanalytikk for å forstå trender og vekst. Naviger via <strong>Innsikt</strong> i toppmenyen. Kun tilgjengelig for ADMIN-brukere.</p>

    <h3>9.1 Vekstmålinger</h3>
    <p>Sammenlign ytelse over tid:</p>
    <table>
        <thead><tr><th>Sammenligning</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Uke mot uke</strong></td><td>Denne uken sammenlignet med forrige uke</td></tr>
            <tr><td><strong>Måned mot måned</strong></td><td>Denne måneden sammenlignet med forrige måned</td></tr>
            <tr><td><strong>År mot år</strong></td><td>I år sammenlignet med i fjor</td></tr>
        </tbody>
    </table>

    <h3>9.2 Datoperiode</h3>
    <p>Velg egendefinert datoperiode eller bruk hurtigknappene:</p>
    <ul>
        <li><strong>I dag</strong>, <strong>Denne uken</strong>, <strong>Denne måneden</strong>, <strong>I år</strong></li>
        <li><strong>Forrige måned</strong>, <strong>I fjor</strong></li>
    </ul>

    <h3>9.3 Avdelingsfilter</h3>
    <p>Velg spesifikk avdeling eller se samlet analytikk for alle avdelinger.</p>
</div>

<!-- 10. Innstillinger -->
<div class="section" id="kap10">
    <h2><span class="chapter-num">10</span> Innstillinger</h2>
    <p>Alle konfigurasjoner for virksomheten. Naviger via <strong>Innstillinger</strong> i toppmenyen.</p>

    <h3>10.1 Stengte dager</h3>
    <p>Planlegg dager virksomheten skal holde stengt.</p>
    <ol>
        <li>Klikk <strong>Legg til stengte dager</strong></li>
        <li>Velg <strong>fra-dato</strong> og <strong>til-dato</strong></li>
        <li>Skriv en <strong>melding</strong> som vises til kunder (f.eks. «Stengt for ferie»)</li>
        <li>Klikk <strong>Lagre</strong></li>
    </ol>
    <p>Nettbestilling blokkeres automatisk på stengte dager.</p>

    <h3>10.2 Åpningstider</h3>
    <p>Konfigurer åpningstider for hver ukedag.</p>
    <table>
        <thead><tr><th>Innstilling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Restaurantåpning</strong></td><td>Klokkeslett restauranten åpner</td></tr>
            <tr><td><strong>Restaurantstenging</strong></td><td>Klokkeslett restauranten stenger</td></tr>
            <tr><td><strong>Kjøkkenstenging</strong></td><td>Klokkeslett kjøkkenet stenger (vanligvis før restauranten)</td></tr>
        </tbody>
    </table>
    <p>Åpningstidene bestemmer når nettbestilling er tilgjengelig.</p>

    <h3>10.3 Levering</h3>
    <p>Konfigurer leveringsinnstillinger:</p>
    <ul>
        <li><strong>Minstbeløp</strong> – Minste ordrebeløp for levering</li>
        <li><strong>Leveringsdager</strong> – Aktiver/deaktiver levering per ukedag</li>
        <li><strong>Leveringstider</strong> – Tidsvinduer for levering per dag (fra/til)</li>
    </ul>

    <h3>10.4 Bord</h3>
    <p>Administrer spisebord for Spise her-ordrer.</p>
    <ul>
        <li><strong>Legg til bord</strong> – Angi bordnummer, kapasitet og beskrivelse</li>
        <li><strong>Slett bord</strong> – Fjern bord som ikke lenger er i bruk</li>
    </ul>

    <h3>10.5 Merverdiavgift (MVA)</h3>
    <p>Se gjeldende MVA-satser for de ulike ordretypene:</p>
    <table>
        <thead><tr><th>Ordretype</th><th>Typisk MVA-sats</th></tr></thead>
        <tbody>
            <tr><td><strong>Spise her</strong></td><td>25 %</td></tr>
            <tr><td><strong>Ta med / Henting</strong></td><td>15 %</td></tr>
            <tr><td><strong>Levering</strong></td><td>15 %</td></tr>
        </tbody>
    </table>
    <div class="info-box">
        <strong>Merk:</strong> Endring av MVA-satser kan kun gjøres av SUPERADMIN.
    </div>

    <h3>10.6 Firma</h3>
    <p>Oppdater firmainformasjon:</p>
    <ul>
        <li>Firmanavn (hentet fra Brønnøysundregistrene)</li>
        <li>Organisasjonsnummer</li>
        <li>Adresse (gate, postnummer, by)</li>
        <li>Telefon og e-post</li>
    </ul>

    <h3>10.7 Avdelinger</h3>
    <p>Se og administrer alle avdelinger/filialer i virksomheten. Hver avdeling har:</p>
    <ul>
        <li>Avdelingsnavn</li>
        <li>Organisasjonsnummer</li>
        <li>Adresse, telefon og e-post</li>
    </ul>

    <h3>10.8 Terminaler</h3>
    <p>Oversikt over alle registrerte kasseterminaler med:</p>
    <ul>
        <li>System-ID-nummer</li>
        <li>Terminal-ID</li>
        <li>Terminaltype (POS, KIOSK)</li>
        <li>Enhetsnavn</li>
        <li>Opprettelsesdato</li>
    </ul>

    <h3>10.9 Hurtigkontroller</h3>
    <p>Følgende hurtighandlinger er tilgjengelige under Innstillinger:</p>
    <table>
        <thead><tr><th>Handling</th><th>Beskrivelse</th></tr></thead>
        <tbody>
            <tr><td><strong>Steng henting midlertidig</strong></td><td>Steng nettbestilling for resten av dagen med egendefinert melding</td></tr>
            <tr><td><strong>Åpne henting</strong></td><td>Gjenåpne henting som ble midlertidig stengt</td></tr>
            <tr><td><strong>Endre tidligste hentetid</strong></td><td>Juster minimum forberedelsestid for henting</td></tr>
            <tr><td><strong>Steng retter</strong></td><td>Marker spesifikke retter som midlertidig utilgjengelige</td></tr>
            <tr><td><strong>Åpne retter</strong></td><td>Gjør tidligere stengte retter tilgjengelige igjen</td></tr>
        </tbody>
    </table>
</div>

<!-- 11. Support -->
<div class="section" id="kap11">
    <h2><span class="chapter-num">11</span> Support</h2>
    <p>Supportseksjonen gir tilgang til hjelpemateriell og dokumentasjon.</p>

    <h3>11.1 Brukerveiledning (Kasse)</h3>
    <p>Komplett brukerveiledning for TastyPOS kassesystemet. Dekker daglig bruk av kasseappen, inkludert innlogging, bestilling, betaling, dagsoppgjør m.m. Tilgjengelig som nedlastbar PDF.</p>

    <h3>11.2 Brukerveiledning (Backoffice)</h3>
    <p>Denne veiledningen – komplett guide for Backoffice-administrasjon.</p>

    <h3>11.3 Hjelpevideo</h3>
    <p>Videoopplæring for de viktigste funksjonene i systemet.</p>

    <h3>11.4 Bokettersyn</h3>
    <p>Informasjon om bokettersyn fra skattemyndighetene:</p>
    <ul>
        <li>Retningslinjer for kontroll fra Skatteetaten</li>
        <li>Dokumentasjon av elektronisk journal</li>
        <li>Instruksjoner for eksport av SAF-T-rapport</li>
        <li>Referanser til Z-rapporter for revisjon</li>
        <li>Kvitteringskrav og forpliktelser</li>
        <li>Identifikasjonsinformasjon for kassasystemet</li>
    </ul>
</div>

</main>
</div>

<!-- Footer -->
<div class="footer">
    <p>Denne brukerveiledningen beskriver Backoffice-systemets oppbygging og funksjonalitet per januar 2026.</p>
    <p>For spørsmål eller support, kontakt <strong>CAGISTA AS</strong>.</p>
</div>
</div>
</template>

<style>
.bv-wrapper {
    /*
     * Brand accents. A FILL is identity and never moves — an orange button is orange in both themes —
     * but the same hue AS TYPE has to, because #E65100 on a near-black page is not legible. Same split
     * the --bo-*-ink tokens make, and the reason this palette is not simply four colours.
     */
    --orange: #F57C00;
    --orange-press: #E65100;    /* the accent as a pressed fill */
    --accent-ink: #E65100;      /* the accent as TYPE */
    --tint: #FFF3E0;            /* the pale accent panel behind a callout */
    --green: #4CAF50;
    --refund-ink: #6A1B9A;

    /*
     * The two dark bands — the page header and the table head — are dark in BOTH themes, so they hold
     * literal values and the text on them is literally white. Reading either off a surface token is
     * what broke this page: --white resolves to #1c1f24 in dark, so the table head was near-black text
     * on near-black, and the header gradient ran from dark to the near-WHITE ink colour.
     */
    --band: #2C2C2C;
    --band-2: #1a1a2e;
    --on-band: #ffffff;

    --ink: var(--bo-ink);       /* headings and emphasis */
    --gray-100: var(--bo-surface-2);
    --gray-200: var(--bo-line);
    --gray-300: var(--bo-line);
    --gray-600: var(--bo-muted);
    --gray-800: var(--bo-ink-2);   /* body copy */
    --card: var(--bo-surface);     /* CARD SURFACES ONLY — type on a band uses --on-band */
    --shadow: 0 2px 8px rgba(0,0,0,0.08);
    --shadow-lg: 0 4px 16px rgba(0,0,0,0.12);
    --radius: 8px;

    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.7;
    color: var(--gray-800);
    background: var(--gray-100);
    scroll-behavior: smooth;
}

/* Only the roles that cannot keep their light value: an accent used as type, and the pale panel it
   sits on. Everything else already comes from the --bo-* tokens, which move on their own. */
[data-bs-theme="dark"] .bv-wrapper {
    --accent-ink: #FFA552;
    --tint: #3A2412;
    --refund-ink: #D6A8EE;
}

.bv-wrapper *, .bv-wrapper *::before, .bv-wrapper *::after {
    box-sizing: border-box;
}

/* Header */
.bv-wrapper .header {
    background: linear-gradient(135deg, var(--band) 0%, var(--band-2) 100%);
    color: var(--on-band);
    padding: 48px 0 40px;
    text-align: center;
    position: relative;
}
.bv-wrapper .header::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0; right: 0;
    height: 40px;
    background: var(--gray-100);
    border-radius: 50% 50% 0 0 / 100% 100% 0 0;
}
.bv-wrapper .header h1 {
    font-size: 2.4em;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
}
.bv-wrapper .header .subtitle {
    font-size: 1.15em;
    opacity: 0.85;
    font-weight: 300;
}
.bv-wrapper .header-meta {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 32px;
    font-size: 0.9em;
    opacity: 0.7;
}

/* Layout */
.bv-wrapper .container {
    display: flex;
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 24px;
    gap: 40px;
}

/* Sidebar TOC */
.bv-wrapper .sidebar {
    width: 280px;
    flex-shrink: 0;
    position: sticky;
    top: calc(var(--bo-nav-offset, 0px) + 24px);   /* clears the pinned navbar */
    align-self: flex-start;
    max-height: calc(100vh - var(--bo-nav-offset, 0px) - 48px);
    overflow-y: auto;
    padding: 24px 0;
}
.bv-wrapper .sidebar::-webkit-scrollbar { width: 4px; }
.bv-wrapper .sidebar::-webkit-scrollbar-thumb { background: var(--gray-300); border-radius: 4px; }

.bv-wrapper .toc {
    background: var(--card);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 20px;
}
.bv-wrapper .toc h3 {
    font-size: 0.85em;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--gray-600);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--orange);
}
.bv-wrapper .toc ol {
    list-style: none;
    counter-reset: toc-counter;
    padding-left: 0;
}
.bv-wrapper .toc ol li {
    counter-increment: toc-counter;
    list-style: none;
    display: block !important;
    width: 100% !important;
    float: none !important;
}
.bv-wrapper .toc ol li a {
    display: block;
    padding: 6px 8px;
    color: var(--gray-800);
    text-decoration: none;
    font-size: 0.88em;
    border-radius: 4px;
    transition: all 0.2s;
    border-left: 3px solid transparent;
}
.bv-wrapper .toc ol li a:hover {
    background: var(--tint);
    color: var(--accent-ink);
    border-left-color: var(--orange);
}
.bv-wrapper .toc ol li a::before {
    content: counter(toc-counter) ". ";
    font-weight: 600;
    color: var(--orange);
}

/* Main content */
.bv-wrapper .main {
    flex: 1;
    min-width: 0;
    padding: 24px 0 80px;
}

/* Sections */
.bv-wrapper .section {
    background: var(--card);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 40px 48px;
    margin-bottom: 24px;
}

.bv-wrapper h2 {
    font-size: 1.6em;
    color: var(--ink);
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 3px solid var(--orange);
    display: flex;
    align-items: center;
    gap: 12px;
}
.bv-wrapper h2 .chapter-num {
    background: var(--orange);
    color: var(--on-band);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6em;
    font-weight: 700;
    flex-shrink: 0;
}

.bv-wrapper h3 {
    display: block;
    width: 100%;
    font-size: 1.15em;
    color: var(--accent-ink);
    margin: 28px 0 12px;
}
.bv-wrapper h3:first-of-type { margin-top: 0; }

.bv-wrapper h4 {
    font-size: 1em;
    color: var(--gray-800);
    margin: 20px 0 8px;
    font-weight: 600;
}

.bv-wrapper p { margin-bottom: 12px; }

/* Lists */
.bv-wrapper .section ul,
.bv-wrapper .section ol {
    display: block !important;
    margin: 8px 0 16px 24px !important;
    padding-left: 20px !important;
    float: none !important;
    flex-direction: column !important;
}
.bv-wrapper .section ul { list-style-type: disc !important; }
.bv-wrapper .section ol { list-style-type: decimal !important; }
.bv-wrapper .section li {
    display: list-item !important;
    float: none !important;
    width: 100% !important;
    margin-bottom: 4px !important;
}
.bv-wrapper .section > ul > li,
.bv-wrapper .section > ol > li {
    padding: 0 !important;
}
.bv-wrapper .section ul > li { list-style-type: disc !important; }
.bv-wrapper .section ol > li { list-style-type: decimal !important; }
.bv-wrapper li strong { color: var(--ink); }

/* Tables */
.bv-wrapper table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 0.95em;
}
.bv-wrapper thead th {
    background: var(--band);
    color: var(--on-band);
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 0.9em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.bv-wrapper thead th:first-child { border-radius: var(--radius) 0 0 0; }
.bv-wrapper thead th:last-child { border-radius: 0 var(--radius) 0 0; }
.bv-wrapper tbody td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--gray-200);
}
.bv-wrapper tbody tr:hover { background: var(--tint); }
.bv-wrapper tbody tr:last-child td:first-child { border-radius: 0 0 0 var(--radius); }
.bv-wrapper tbody tr:last-child td:last-child { border-radius: 0 0 var(--radius) 0; }

/* Info boxes */
.bv-wrapper .info-box {
    background: var(--tint);
    border-left: 4px solid var(--orange);
    padding: 16px 20px;
    border-radius: 0 var(--radius) var(--radius) 0;
    margin: 16px 0;
    font-size: 0.95em;
}

/* Footer */
.bv-wrapper .footer {
    text-align: center;
    padding: 32px 24px;
    color: var(--gray-600);
    font-size: 0.9em;
    border-top: 1px solid var(--gray-200);
    margin-top: 40px;
}

/* Responsive */
@media (max-width: 960px) {
    .bv-wrapper .container { flex-direction: column; }
    .bv-wrapper .sidebar {
        width: 100%;
        position: static;
        max-height: none;
        padding: 0;
    }
    .bv-wrapper .section { padding: 24px; }
    .bv-wrapper .header h1 { font-size: 1.8em; }
    .bv-wrapper .header-meta { flex-direction: column; gap: 4px; }
}

@media print {
    .bv-wrapper .sidebar { display: none; }
    .bv-wrapper .section { box-shadow: none; break-inside: avoid; }
    .bv-wrapper .header::after { display: none; }
}
</style>
